// 自帶 AI（plan/SPEC.md#byo-ai-ui、#byo-ai-api）：使用者填自己的 OpenAI / Anthropic / Gemini key，平台代呼叫做場景描述與標籤。
//   - 主體同雲端 AI（組織或 personal home，跟頁首範圍）；新增 / 換 key / 刪除只有 org_admin / home owner（伺服器端 + 5 分鐘內重新驗證，SDK 自動跳）
//   - key 存完不再顯示（只有末 4 碼）；平台先用 key 打供應商不花錢的列模型端點驗證
//   - 同意書摘要＋〔看完整說明〕；逐台開關（開了的攝影機 Kinio 雲端 AI 跳過、不扣額度）；本月次數與上限
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { KinioError } from '@kinio/web-sdk';
import { useScope } from '../../lib/scope';
import { useDevices } from '../../lib/useDevices';
import { fullName } from '../../lib/devices';
import { aiByoApi, type ByoCredential, type ByoProvider } from '../../lib/planeApi';
import { stamp } from '../../lib/timefmt';
import { useAiSubjects, type Subj } from './CloudAiSection';
import { useEmailVerifyPrompt } from '../../components/EmailVerifyPrompt';

const PROVIDERS: ByoProvider[] = ['openai', 'anthropic', 'gemini'];

function errText(t: (k: string, o?: Record<string, unknown>) => string, e: unknown): string {
  if (e instanceof KinioError) return t(`aiByo.err_${e.code}`, { defaultValue: t('billing.err_generic', { code: e.code }) });
  return String(e);
}

export function ByoAiSection() {
  const { t } = useTranslation();
  const { setScope } = useScope();
  const { subjects, key, subject } = useAiSubjects();
  if (!subjects.length) return <p className="muted">{t('aiCloud.no_subject')}</p>;
  return (
    <section className="card stack" data-testid="byo-ai">
      <h2 className="section-title">{t('aiByo.title')}</h2>
      <p className="muted small">{t('aiByo.help')}</p>
      {subject ? (
        <p className="muted small">{t('billing.capsule', { name: subject.name })}</p>
      ) : (
        <div className="row">
          {subjects.map((s) => (
            <button key={`${s.kind}:${s.id}`} className="btn btn-sm" onClick={() => setScope(`${s.kind}:${s.id}`, '', { announce: t('scope.switched_to', { name: s.name }) })}>
              {s.name}
            </button>
          ))}
        </div>
      )}
      {subject && <SubjectByo key={key} s={subject} />}
    </section>
  );
}

function SubjectByo({ s }: { s: Subj }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['ai-byo', s.kind, s.id], queryFn: () => aiByoApi.list(s) });
  const usage = useQuery({ queryKey: ['ai-byo-usage', s.kind, s.id], queryFn: () => aiByoApi.usage(s), refetchInterval: 30_000 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [models, setModels] = useState<string[]>([]);
  const verify = useEmailVerifyPrompt(); // 組織加 BYO 憑證遇 403 EMAIL_NOT_VERIFIED（platform#176）
  const run = async (f: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await f();
      void qc.invalidateQueries({ queryKey: ['ai-byo', s.kind, s.id] });
      void qc.invalidateQueries({ queryKey: ['ai-byo-usage', s.kind, s.id] });
      void qc.invalidateQueries({ queryKey: ['ai-consent', s.kind, s.id] });
    } catch (e) {
      if (!verify.ask(e)) setErr(errText(t, e));
    }
    setBusy(false);
  };
  if (list.isLoading) return <p className="muted">{t('common.loading')}</p>;
  if (list.error) return <p className="error">{errText(t, list.error)}</p>;
  const c = list.data?.credentials[0] ?? null;
  return (
    <>
      {verify.prompt}
      {err && (
        <p className="error" role="alert" data-testid="byo-error">
          {err}
        </p>
      )}
      {c ? (
        <Credential s={s} c={c} models={models} busy={busy} run={run} usage={usage.data?.usage ?? null} />
      ) : s.canConsent ? (
        <AddKey s={s} textVersion={list.data?.text_version ?? ''} busy={busy} onAdd={(m) => setModels(m)} run={run} />
      ) : (
        <p className="muted">{t(s.kind === 'org' ? 'aiCloud.need_org_admin' : 'aiCloud.need_owner')}</p>
      )}
    </>
  );
}

function AddKey(props: { s: Subj; textVersion: string; busy: boolean; onAdd: (models: string[]) => void; run: (f: () => Promise<unknown>) => Promise<void> }) {
  const { t } = useTranslation();
  const [provider, setProvider] = useState<ByoProvider>('openai');
  const [apiKey, setApiKey] = useState('');
  const [agree, setAgree] = useState(false);
  return (
    <div className="stack" data-testid="byo-add">
      <label>
        {t('aiByo.provider')}{' '}
        <select value={provider} onChange={(e) => setProvider(e.target.value as ByoProvider)} data-testid="byo-provider">
          {PROVIDERS.map((p) => (
            <option key={p} value={p}>
              {t(`aiByo.provider_${p}`)}
            </option>
          ))}
        </select>
      </label>
      <label>
        {t('aiByo.api_key')}{' '}
        <input type="password" autoComplete="off" spellCheck={false} value={apiKey} onChange={(e) => setApiKey(e.target.value.trim())} data-testid="byo-key" />
      </label>
      <p className="muted small">{t('aiByo.key_help')}</p>
      <p>{t('aiByo.consent_summary', { provider: t(`aiByo.provider_${provider}`) })}</p>
      <details className="card">
        <summary>{t('aiCloud.consent_more')}</summary>
        <div style={{ whiteSpace: 'pre-line' }}>{t('aiByo.consent_body')}</div>
      </details>
      <label>
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="byo-agree" /> {t('aiByo.agree')}
      </label>
      <div>
        <button
          className="btn btn-primary"
          data-testid="byo-save"
          disabled={!agree || props.busy || apiKey.length < 20}
          onClick={() =>
            void props.run(async () => {
              const r = await aiByoApi.create(props.s, { provider, api_key: apiKey, consent_text_version: props.textVersion });
              setApiKey('');
              props.onAdd(r.models);
            })
          }
        >
          {t('aiByo.add')}
        </button>{' '}
        <span className="muted small">{t('aiByo.add_help')}</span>
      </div>
    </div>
  );
}

function Credential(props: {
  s: Subj;
  c: ByoCredential;
  models: string[];
  busy: boolean;
  usage: { calls: number; failures: Record<string, number> } | null;
  run: (f: () => Promise<unknown>) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { s, c, busy, run } = props;
  const { cameras, tree } = useDevices();
  const [hour, setHour] = useState(c.max_per_hour);
  const [day, setDay] = useState(c.max_per_day);
  const [rekey, setRekey] = useState('');
  const cams = cameras.filter((x) => (s.kind === 'home' ? x.homeId === s.id : true));
  const models = props.models.includes(c.model) ? props.models : [c.model, ...props.models];
  const statusClass = c.status === 'active' ? 'badge-ok' : 'badge-warn';
  const failures = Object.entries(props.usage?.failures ?? {});
  return (
    <div className="stack" data-testid="byo-credential">
      <p>
        <strong>{t(`aiByo.provider_${c.provider}`)}</strong> · <code>{c.key_hint}</code>{' '}
        <span className={`badge ${statusClass}`} data-testid="byo-status">
          {t(`aiByo.status_${c.status}`)}
        </span>
        {c.last_used_at && <span className="muted small"> · {t('aiByo.last_used', { at: stamp(c.last_used_at) })}</span>}
        {c.last_error_code && <span className="muted small"> · {t(`aiByo.fail_${c.last_error_code}`, { defaultValue: c.last_error_code })}</span>}
      </p>
      {c.status === 'invalid' && <p className="notice small">{t('aiByo.invalid_help')}</p>}
      <label>
        {t('aiByo.model')}{' '}
        <select
          value={c.model}
          disabled={!s.canConsent || busy}
          onChange={(e) => void run(() => aiByoApi.update(s, c.id, { expected_version: c.version, model: e.target.value }))}
          data-testid="byo-model"
        >
          {models.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </label>
      <div data-testid="byo-usage">
        {t('aiByo.usage', { calls: props.usage?.calls ?? 0 })}
        {failures.length > 0 && (
          <span className="muted small">
            {' '}
            · {failures.map(([k, n]) => `${t(`aiByo.fail_${k}`, { defaultValue: k })} ${n}`).join('、')}
          </span>
        )}
        <div className="muted small">{t('aiByo.cost_note')}</div>
      </div>
      <div className="toolbar wrap">
        <label>
          {t('aiByo.max_per_hour')} <input type="number" min={1} max={600} value={hour} disabled={!s.canConsent} onChange={(e) => setHour(Number(e.target.value))} />
        </label>
        <label>
          {t('aiByo.max_per_day')} <input type="number" min={1} max={14400} value={day} disabled={!s.canConsent} onChange={(e) => setDay(Number(e.target.value))} />
        </label>
        {s.canConsent && (
          <button
            className="btn btn-sm"
            disabled={busy || (hour === c.max_per_hour && day === c.max_per_day)}
            onClick={() => void run(() => aiByoApi.update(s, c.id, { expected_version: c.version, max_per_hour: hour, max_per_day: day }))}
          >
            {t('common.save')}
          </button>
        )}
      </div>
      <div>
        <div className="small muted">{t('aiByo.devices_help')}</div>
        <div className="toolbar wrap" data-testid="byo-devices">
          {cams.map((cam) => {
            const on = c.device_refs.includes(cam.aid);
            return (
              <label key={cam.key} className={`chip ${on ? 'on' : ''}`}>
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!s.canConsent || busy}
                  onChange={() =>
                    void run(() =>
                      aiByoApi.devices(s, {
                        expected_version: c.version,
                        device_refs: on ? c.device_refs.filter((x) => x !== cam.aid) : [...c.device_refs, cam.aid],
                      }),
                    )
                  }
                />{' '}
                {fullName(cam, tree)}
              </label>
            );
          })}
        </div>
      </div>
      {s.canConsent && (
        <div className="toolbar wrap">
          <input
            type="password"
            autoComplete="off"
            spellCheck={false}
            placeholder={t('aiByo.rekey_placeholder')}
            value={rekey}
            onChange={(e) => setRekey(e.target.value.trim())}
            data-testid="byo-rekey"
          />
          <button
            className="btn btn-sm"
            disabled={busy || rekey.length < 20}
            onClick={() =>
              void run(async () => {
                await aiByoApi.update(s, c.id, { expected_version: c.version, api_key: rekey });
                setRekey('');
              })
            }
          >
            {t('aiByo.rekey')}
          </button>
          <button className="btn btn-sm btn-danger" disabled={busy} onClick={() => void run(() => aiByoApi.remove(s, c.id))} data-testid="byo-delete">
            {t('aiByo.delete')}
          </button>
          <span className="muted small">{t('aiByo.delete_help')}</span>
        </div>
      )}
    </div>
  );
}
